import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Bold, Italic, List } from "lucide-react";
import { countries } from "@/lib/countries";
import { useTranslation,Trans } from "react-i18next";

import {AutoTranslate} from "@/lib/autoTranslate.tsx";
import { clearFormDraft, readFormDraft, writeFormDraft } from "@/lib/formDraftStorage";



interface WorkExperience {
  id: string;
  companyName: string;
  location: string;
  jobTitle: string;
  startMonth: string;
  startYear: string;
  endMonth: string;
  endYear: string;
  responsibilities: string;
  noNumbers: boolean;
  presentlyWorking: boolean;
}

interface WorkExperienceFormProps {
  onSubmit: (data: WorkExperience[]) => void;
  language: string;
}

const createEmptyExperience = (): WorkExperience => ({
  id: crypto.randomUUID(),
  companyName: "",
  location: "",
  jobTitle: "",
  startMonth: "",
  startYear: "",
  endMonth: "",
  endYear: "",
  responsibilities: "",
  noNumbers: false,
  presentlyWorking: false
});

const normalizeWorkExperienceDraft = (draft: WorkExperience[]): WorkExperience[] => {
  if (!Array.isArray(draft) || draft.length === 0) {
    return [createEmptyExperience()];
  }

  return draft.map((experience) => ({
    ...createEmptyExperience(),
    ...experience,
    id: experience.id || crypto.randomUUID()
  }));
};

export function WorkExperienceForm({ onSubmit, language }: WorkExperienceFormProps) {
  const transText = useTranslation().t('autoTranslate', { returnObjects: true });
  const { i18n, t } = useTranslation();


  const [alertNumNotification,setAlertNumNotification] = useState(useTranslation().t('forms.experience.alertNumbers'));

  //below are the objects storing the translated strings
  const labelsTrans = useTranslation().t('forms.experience.labels', { returnObjects: true });
  const placeholdersTrans = useTranslation().t('forms.experience.placeholders', { returnObjects: true });
  const buttonsTrans = useTranslation().t('forms.experience.buttons', { returnObjects: true });
  const monthOptions = JSON.parse(t("forms.education.months")) as string[];
  const yearOptions = Array.from({ length: 80 }, (_, i) => (new Date().getFullYear() - i).toString());

  /*

  const experienceTrans = useTranslation().t('forms.experience.labels.experience');
  const experienceTrans = useTranslation().t('forms.experience.labels.experience');
  const experienceTrans = useTranslation().t('forms.experience.labels.experience');
  const experienceTrans = useTranslation().t('forms.experience.labels.experience');*/
  const [resumeDownloaded, setResumeDownloaded] = useState(false);
  //useEffect(() => {
  //  setAlertNumNotification(useTranslation().t('forms.experience.alertNumbers'));
  //}, [language])
  const [experiences, setExperiences] = useState<WorkExperience[]>(() => (
    normalizeWorkExperienceDraft(readFormDraft('workExperience', [createEmptyExperience()]))
  ));

  useEffect(() => {
    writeFormDraft('workExperience', experiences);
  }, [experiences]);

  const addExperience = () => {
    setExperiences([...experiences, createEmptyExperience()]);
  };

  const removeExperience = (id: string) => {
    setExperiences(experiences.filter(exp => exp.id !== id));
  };

  const updateExperience = (id: string, field: keyof WorkExperience, value: any) => {
    //console.log(id);
    switch(field) {
      case "presentlyWorking":
        setExperiences(experiences.map((exp) =>
          exp.id === id ? { ...exp, endMonth: "", endYear: "", [field]: value } : exp));
        break;
      default:
        setExperiences(experiences.map((exp) =>
          exp.id === id ? { ...exp, [field]: value } : exp));
        break;
    }
  };

  const applyFormatting = (id: string, formatType: 'bold' | 'italic' | 'bullet') => {
    const exp = experiences.find(e => e.id === id);
    if (!exp) return;

    const textarea = document.querySelector(`#responsibilities-${id}`) as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = exp.responsibilities.substring(start, end);

    let newText = exp.responsibilities;

    if (selectedText) {
      let formattedText = selectedText;

      switch (formatType) {
        case 'bold':
          formattedText = `**${selectedText}**`;
          break;
        case 'italic':
          formattedText = `*${selectedText}*`;
          break;
        case 'bullet':
          formattedText = selectedText.split('\n').map(line =>
            line.trim() ? `• ${line.trim()}` : line
          ).join('\n');
          break;
      }

      newText = exp.responsibilities.substring(0, start) + formattedText + exp.responsibilities.substring(end);
    } else {
      // If no text selected, add at cursor position
      switch (formatType) {
        case 'bullet':
          newText = exp.responsibilities.substring(0, start) + '\n• ' + exp.responsibilities.substring(start);
          break;
      }
    }

    updateExperience(id, 'responsibilities', newText);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      const newPosition = formatType === 'bullet' ? start + 3 : start + newText.length - exp.responsibilities.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    for (const exp of experiences) {
      if (!exp.noNumbers && !exp.responsibilities.match(/\d+/)) {
        alert(alertNumNotification);
        return;
      }
    }

    clearFormDraft('workExperience');
    onSubmit(experiences);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6 bg-card rounded-lg border">
      <h2 className="text-2xl font-bold">{useTranslation().t('forms.experience.labels.workExperience')}</h2>

      {experiences.map((exp, index) => (
        <div key={exp.id} className="space-y-4 p-4 border rounded-lg relative">
          {experiences.length > 1 && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2"
              aria-label="Remove work experience"
              onClick={() => removeExperience(exp.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}

          <h3 className="font-semibold">{labelsTrans['experience']} {index + 1}</h3>

          <div className="space-y-2">
            <Label htmlFor={`company-${exp.id}`}>{labelsTrans["companyName"]}</Label>
            <Input
              id={`company-${exp.id}`}
              required
              value={exp.companyName}
              onChange={(e) => updateExperience(exp.id, "companyName", e.target.value)}
              placeholder={placeholdersTrans["companyName"]}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`location-${exp.id}`}>{labelsTrans["location "]}</Label>
            <Select
              required
              value={exp.location}
              onValueChange={(value) => updateExperience(exp.id, "location", value)}
            >
              <SelectTrigger id={`location-${exp.id}`}>
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Remote">{labelsTrans["Remote"]}</SelectItem>
                {countries.map((country) => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`job-title-${exp.id}`}>{AutoTranslate('Job Title')} {labelsTrans["jobTitle"]}</Label>
            <Input
              id={`job-title-${exp.id}`}
              required
              value={exp.jobTitle}
              onChange={(e) => {console.log(language);updateExperience(exp.id, "jobTitle", e.target.value)}}
              placeholder={placeholdersTrans["jobTitle"]}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`start-month-${exp.id}`}>{labelsTrans["startDate"]}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select value={exp.startMonth} onValueChange={(value) => updateExperience(exp.id, "startMonth", value)}>
                  <SelectTrigger id={`start-month-${exp.id}`}>
                    <SelectValue placeholder={AutoTranslate(`Month`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((month) => (
                      <SelectItem key={month} value={month}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={exp.startYear} onValueChange={(value) => updateExperience(exp.id, "startYear", value)}>
                  <SelectTrigger id={`start-year-${exp.id}`}>
                    <SelectValue placeholder={AutoTranslate(`Year`)} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[260px]">
                    {yearOptions.map((year) => (
                      <SelectItem key={year} value={year}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`end-month-${exp.id}`}>{labelsTrans["endDate"]}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select
                  value={exp.endMonth}
                  onValueChange={(value) => updateExperience(exp.id, "endMonth", value)}
                  disabled={exp.presentlyWorking}
                >
                  <SelectTrigger id={`end-month-${exp.id}`} className={exp.presentlyWorking ? "opacity-50" : ""}>
                    <SelectValue placeholder={exp.presentlyWorking ? buttonsTrans["present"] : AutoTranslate(`Month`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((month) => (
                      <SelectItem key={month} value={month}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={exp.endYear}
                  onValueChange={(value) => updateExperience(exp.id, "endYear", value)}
                  disabled={exp.presentlyWorking}
                >
                  <SelectTrigger id={`end-year-${exp.id}`} className={exp.presentlyWorking ? "opacity-50" : ""}>
                    <SelectValue placeholder={exp.presentlyWorking ? buttonsTrans["present"] : AutoTranslate(`Year`)} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[260px]">
                    {yearOptions.map((year) => (
                      <SelectItem key={year} value={year}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 mt-2">
                <Checkbox
                  id={`presently-working-${exp.id}`}
                  checked={exp.presentlyWorking}
                  onCheckedChange={(checked) => {
                    updateExperience(exp.id, "presentlyWorking", !!checked);

                  }}
                />
                <Label htmlFor={`presently-working-${exp.id}`} className="font-normal cursor-pointer text-sm">{AutoTranslate(`Presently working here`)}</Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`responsibilities-${exp.id}`}>{labelsTrans["tasks"]}</Label>
            <div className="flex gap-2 mb-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label="Bold text"
                onClick={() => applyFormatting(exp.id, 'bold')}
                title="Bold"
              >
                <Bold className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label="Italic text"
                onClick={() => applyFormatting(exp.id, 'italic')}
                title="Italic"
              >
                <Italic className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label="Add bullet points"
                onClick={() => applyFormatting(exp.id, 'bullet')}
                title="Bullet Point"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Textarea
              id={`responsibilities-${exp.id}`}
              required
              value={exp.responsibilities}
              onChange={(e) => updateExperience(exp.id, "responsibilities", e.target.value)}
              placeholder={placeholdersTrans["tasks"]}
              rows={6}
            />
            <p className="text-sm text-muted-foreground">
              {alertNumNotification}
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id={`no-numbers-${exp.id}`}
              checked={exp.noNumbers}
              onCheckedChange={(checked) => updateExperience(exp.id, "noNumbers", !!checked)}
            />
            <Label htmlFor={`no-numbers-${exp.id}`} className="font-normal cursor-pointer">{labelsTrans["noNumbers"]}</Label>
          </div>
        </div>
      ))}

      <Button type="button" variant="outline" onClick={addExperience} className="w-full">
        <Plus className="h-4 w-4 mr-2" />
        {AutoTranslate(`Add More Experience`)} {buttonsTrans["addExperience"]}
      </Button>

      <Button type="submit" className="w-full">{AutoTranslate(`Send`)}</Button>
    </form>
  );
 }